import React, { createContext, useContext, useState, useEffect } from 'react';
import subUserService from '../../services/SubUserService';

const PermissionContext = createContext();

export const PermissionProvider = ({ children }) => {
  const [permissions, setPermissions] = useState({
    isAdmin: true,
    screens: [],
    fields: {}
  });
  const [loading, setLoading] = useState(true);

  const fetchPermissions = async () => {
    try {
      setLoading(true);
      const res = await subUserService.getMyPermissions();
      if (res && res.success) {
        setPermissions({
          isAdmin: res.isAdmin,
          screens: res.screens || [],
          fields: res.fields || {}
        });
      }
    } catch (err) {
      console.error('Error fetching active permissions:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPermissions();
  }, []);

  const hasScreenAccess = (screenName) => {
    if (permissions.isAdmin) return true;
    return permissions.screens.includes(screenName);
  };

  const getFieldPermission = (moduleName, fieldName) => {
    if (permissions.isAdmin) return 'WRITE';
    return permissions.fields?.[moduleName]?.[fieldName] || 'WRITE';
  };

  const hasReadAccess = (moduleName, fieldName) => {
    const access = getFieldPermission(moduleName, fieldName);
    return access === 'READ' || access === 'WRITE';
  };

  const hasWriteAccess = (moduleName, fieldName) => {
    const access = getFieldPermission(moduleName, fieldName);
    return access === 'WRITE';
  };

  const isHidden = (moduleName, fieldName) => {
    const access = getFieldPermission(moduleName, fieldName);
    return access === 'HIDE';
  };

  return (
    <PermissionContext.Provider value={{
      permissions,
      loading,
      hasScreenAccess,
      getFieldPermission,
      hasReadAccess,
      hasWriteAccess,
      isHidden,
      refreshPermissions: fetchPermissions
    }}>
      {children}
    </PermissionContext.Provider>
  );
};

export const usePermission = () => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermission must be used within a PermissionProvider');
  }
  return context;
};
